import { useCallback, useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, num, rupee, pnlClass, istDateTime } from "@/lib/api";
import { TradesTable, AuditTable } from "@/components/LiveTables";

const pf = (v) => (v === null || v === undefined ? "—" : v >= 999 ? "∞" : num(v));

export const DryRunPanel = () => {
  const [st, setSt] = useState(null);
  const [trades, setTrades] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/execution/dry-run").then((r) => setSt(r.data)).catch(() => {});
    api.get("/execution/dry-run/trades").then((r) => setTrades(r.data)).catch(() => {});
    api.get("/execution/dry-run/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);

  const act = async (path, ok) => {
    setBusy(true);
    try { await api.post(path); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!st) return null;
  const m = st.metrics;
  return (
    <>
      <div className="panel col-span-12 border-sky-500/30 p-4" data-testid="panel-dry-run">
        <div className="flex flex-wrap items-center gap-3">
          <FlaskConical size={16} className="text-sky-400" />
          <span className="eyebrow !text-sky-300">Dry run · live engine, simulated broker</span>
          <span data-testid="dry-run-state" className={`rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wider ${st.enabled ? "border-sky-500/50 bg-sky-500/10 text-sky-300" : "border-slate-600 text-slate-400"}`}>
            {st.enabled ? (st.market_open ? "RUNNING" : "ON · WAITING FOR MARKET") : "OFF"}
          </span>
          <div className="ml-auto flex gap-2">
            {st.enabled
              ? <Button data-testid="button-dry-stop" size="sm" variant="outline" disabled={busy} onClick={() => act("/execution/dry-run/stop", "Dry run stopped")} className="h-8 border-slate-600">Stop dry run</Button>
              : <Button data-testid="button-dry-start" size="sm" disabled={busy} onClick={() => act("/execution/dry-run/start", "Dry run started")} className="h-8 bg-sky-600 text-white hover:bg-sky-500">Start dry run</Button>}
            <Button data-testid="button-dry-clear" size="sm" variant="ghost" disabled={busy || st.enabled} onClick={() => act("/execution/dry-run/clear", "Dry-run history cleared")} className="h-8 text-xs text-slate-400">Clear history</Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Every signal goes through the same entry rules, limit-order chase, recovery and exits as live trading, but orders are only simulated against real Dhan bid/ask quotes and logged. It runs every strategy enabled for auto-trading, with the same max-2-open, opposite-trade and margin rules (margin is checked against your capital setting). Nothing is sent to your broker, and no gates are required.
          {st.started_at && <span className="num text-slate-500"> Started {istDateTime(st.started_at)}.</span>}
        </p>
        <div className="num mt-3 grid grid-cols-2 gap-3 text-xs md:grid-cols-6">
          <Cell k="Trades" v={m.trades} id="dry-trades" />
          <Cell k="Win rate" v={`${num(m.win_rate)}%`} />
          <Cell k="Profit factor" v={pf(m.profit_factor)} cls={m.profit_factor >= 2 ? "up" : ""} id="dry-pf" />
          <Cell k="Net P&L" v={rupee(m.net_pnl)} cls={pnlClass(m.net_pnl)} id="dry-net" />
          <Cell k="Today" v={rupee(st.day_pnl)} cls={pnlClass(st.day_pnl)} />
          <Cell k="Unfilled / aborted" v={st.aborted} id="dry-aborted" />
        </div>
      </div>
      <TradesTable title="Dry-run trades" rows={trades} id="table-dry-trades" />
      <AuditTable rows={audit} id="panel-dry-audit" title="Dry-run log · orders that would have been placed" empty="No dry-run events yet." />
    </>
  );
};

const Cell = ({ k, v, cls = "text-slate-100", id }) => (
  <div><div className="eyebrow !text-[10px]">{k}</div><div data-testid={id} className={`text-lg font-semibold ${cls}`}>{v}</div></div>
);
